import React, { useEffect, useState } from 'react';
import PropTypes from "prop-types";
import SelectInput from "../SelectInput";
import {
    dateFromState,
    dateToState,
    getDurationOptions,
    getTimeRangeOptions,
    startOfNextDay
} from "../../../Helpers/DateTimeHelper";
import { addMinutes, differenceInMinutes, format, isBefore, parse, startOfDay } from "date-fns";
import { intervalFromState } from "../../../Helpers/IntervalHelper";
import AvailabilityIntervalSelectorDisabled from "./AvailabilityIntervalSelectorDisabled";

/**
 * Provides to adjacent select boxes to allow the user to directly
 * select the start and end time of a time range
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const AvailabilityIntervalSelector = (props) => {

    const [startTime, setStartTime] = useState(null);
    const [duration, setDuration] = useState(30);

    const [startTimeOptions, setStartTimeOptions] = useState([]);
    const [endTimeOptions, setEndTimeOptions] = useState([]);
    const [durationOptions, setDurationOptions] = useState([]);

    // Convert the selected interval from a start/end to start/duration
    useEffect(() => {
        if (props.selectedInterval) {
            let selectedIntervalDate = intervalFromState(props.selectedInterval);
            setStartTime(selectedIntervalDate.start);
            setDuration(differenceInMinutes(selectedIntervalDate.end, selectedIntervalDate.start));
        }
    }, [props.selectedInterval]);

    /**
     * Determine the available select options whenever the intervals change
     */
    useEffect(() => {
        setStartTimeOptions(getTimeRangeOptionsForSelector(true));

        if (props.useDuration) {
            setDurationOptions(getDurationOptions(props.maxDuration, props.selectedInterval, props.blockedIntervals));
        } else {
            setEndTimeOptions(getTimeRangeOptionsForSelector(false));
        }
    }, [props.availableIntervals, props.blockedIntervals, props.selectedInterval])

    const getTimeRangeOptionsForSelector = (forStart = false) => {
        if (!props.availableIntervals || !props.availableIntervals.length) {
            return [];
        }

        // Either show available intervals for the main booking date, or for
        // the date of the selected interval.
        // This is done because user might choose an interval on a subsequent
        // day if they've paged ahead on the timeline
        const dateToShowAvailabilityFor = (props.selectedInterval?.start)
            ? dateFromState(props.selectedInterval.start)
            : dateFromState(props.bookingDate);

        let boundary = {
            start: startOfDay(dateToShowAvailabilityFor),
            end: startOfNextDay(dateToShowAvailabilityFor)
        };

        return getTimeRangeOptions(
            props.availableIntervals,
            boundary,
            15,
            forStart,
            !forStart
        );
    }

    const formatDateForSelect = (date) => {
        if (!date || isNaN(date)) {
            return null;
        }
        return format(date, 'yyyy-MM-dd HH:mm:ss')
    }

    const handleIntervalSelectChange = (e) => {
        let newStartTime = startTime;
        let newDuration = duration;

        if (e.target.name === 'start') {
            newStartTime = parse(e.target.value, 'yyyy-MM-dd HH:mm:ss', new Date());
            setStartTime(newStartTime);
        }

        if (e.target.name === 'duration') {
            newDuration = e.target.value;
            setDuration(newDuration);
        }

        if (e.target.name === 'end' ) {
            let newEndTime = parse(e.target.value, 'yyyy-MM-dd HH:mm:ss', new Date());
            if (isBefore(newStartTime, newEndTime)) {
                newDuration = differenceInMinutes(newEndTime, newStartTime);
                setDuration(newDuration);
            }
        }

        props.onIntervalChange({
            start: dateToState(newStartTime),
            end: dateToState(addMinutes(newStartTime, newDuration))
        }, false, 'From interval selector');
    }

    // TODO - check against current time
    if (props.isEditingExistingBooking && startTime && isBefore(startTime, new Date()) ) {
        return <AvailabilityIntervalSelectorDisabled
            startTime={startTime}
            duration={duration}
            startTimeErrorMessage={props.startTimeErrorMessages}
        />
    }



    return (
        <div className="grid-x">
            <div className={`small-12 ${(props.useVerticalLayout) ? '' : 'medium-5'}`}>
                <label>Start</label>
                <SelectInput
                    name="start"
                    value={formatDateForSelect(startTime)}
                    options={startTimeOptions}
                    onChange={handleIntervalSelectChange}
                    menuPortalTarget={document.body}
                    classNamePrefix='modal-select'
                    disabled={!startTimeOptions.length}
                />
            </div>
            <div className={`small-12 ${(props.useVerticalLayout) ? '' : 'medium-7 padding-left-half'}`}>
                <label>Duration</label>
                {(props.useDuration) && (
                    <SelectInput
                        name="duration"
                        value={duration}
                        options={durationOptions}
                        onChange={handleIntervalSelectChange}
                        menuPortalTarget={document.body}
                        maxMenuHeight={props.durationMenuMaxHeight}
                        classNamePrefix='modal-select'
                        disabled={!startTimeOptions.length}
                    />
                )}

                {(!props.useDuration) && (
                    <SelectInput
                        name="end"
                        value={formatDateForSelect(addMinutes(startTime, duration))}
                        options={endTimeOptions}
                        onChange={handleIntervalSelectChange}
                        menuPortalTarget={document.body}
                        classNamePrefix='modal-select'
                        disabled={!endTimeOptions.length}
                    />
                )}
            </div>
        </div>
    );
}

AvailabilityIntervalSelector.propTypes = {
    availableIntervals: PropTypes.array,
    blockedIntervals: PropTypes.array,
    useDuration: PropTypes.bool,
    useVerticalLayout: PropTypes.bool,
}

AvailabilityIntervalSelector.defaultProps = {
    useDuration: true,
    useVerticalLayout: false,
}

export default AvailabilityIntervalSelector;
